import React, { useEffect, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { selectListItems } from "../../../redux/library/selector";
import { selectCurrent } from "../../../redux/settings/selectors";
import { settings } from "../../../redux/settings/actions";
import { deepCopy } from "../../services/common.service";
import UploadFiles from "../../../models/UploadFile";
import { InputLabelContainer } from "../form/label/containers/input-label.container";
import MediaInfoComponent from "./media-info.component";
import _ from "lodash";

// ---------- Interfaces ----------
interface MediaFile {
  Id: number;
  OwnerTypeId: number;
  OwnerId?: number;
  FileTypeId: number;
  Name?: string;
  Description?: string;
  FileMappingURL: string;
  DisplayOrder?: number;
  IsApproved?: boolean;
}

interface UploadMediaProps {
  handleInnerFormChange: (params: { value: any; field: string }) => void;
  mediaFilesList: MediaFile[];
  ownerTypeId: number;
  ownerId: number;
  folderName: string;
  allowMultipleMedia?: boolean;
  mediaTitle?: string | null;
}

// ---------- Component ----------
export const UploadMedia = React.memo(({
  handleInnerFormChange,
  mediaFilesList,
  ownerTypeId,
  ownerId,
  folderName,
  allowMultipleMedia = true,
  mediaTitle = null
}: UploadMediaProps) => {
  const dispatch = useAppDispatch();
  const { getUploadFiles } = useAppSelector(selectCurrent);
  const libraryData = useAppSelector(selectListItems);

  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>(mediaFilesList || [{
    Id: 0,
    OwnerTypeId: ownerTypeId,
    OwnerId: 0,
    FileTypeId: 0,
    Name: '',
    Description: '',
    FileMappingURL: '',
    DisplayOrder: 0,
    IsApproved: true
  }]);
  const [openFile, setOpenFile] = useState<any>(null);
  const [imageIndex, setImageIndex] = useState<number>(0);
  const [lastChangedIndex, setLastChangedIndex] = useState<number | null>(null);

  // ---------- Helpers ----------
  const getEmptyMediaRow = (): MediaFile => ({
    Id: 0,
    OwnerTypeId: ownerTypeId,
    OwnerId: 0,
    FileTypeId: 0,
    Name: '',
    Description: '',
    FileMappingURL: '',
    DisplayOrder: 0,
    IsApproved: true
  });

  const addNewMediaFilesRow = (files: MediaFile[]): MediaFile[] => {
    const localFiles = deepCopy(files);
    if (
      localFiles.length === 0 ||
      localFiles[localFiles.length - 1].FileTypeId !== 0
    ) {
      localFiles.push(getEmptyMediaRow());
    }
    return localFiles;
  };

  // ---------- Handlers ----------
  const handleDeleteMedia = (index: number) => {
    const updatedFiles = _.clone(mediaFiles);
    _.pullAt(updatedFiles, index);
    setMediaFiles(updatedFiles);
    console.log('updatedFiles', updatedFiles)
    handleInnerFormChange({
      value: { MediaFiles: updatedFiles },
      field: 'MediaMappings'
    });
  };

  const handleClose = () => {
    dispatch(settings.set({ getUploadFiles: 'close' }));
  };

  const handleSubmit = (url: string) => {
    const updated = [...mediaFiles];
    updated[imageIndex].FileMappingURL = url;

    const result = allowMultipleMedia ? addNewMediaFilesRow(updated) : updated;

    setMediaFiles(result);
    handleInnerFormChange({
      value: { MediaFiles: result },
      field: 'MediaMappings'
    });
  };

  const handleFormItemChange = ({ value, field, idx }: { value: any; field: string; idx: number }) => {
    const updated = deepCopy(mediaFiles);
    updated[idx] = { ...updated[idx], [field]: value };
    setMediaFiles(updated);

    if (field === 'FileTypeId') {
      setLastChangedIndex(idx);
    }
  };

  const changeImage = (idx: number, templateField: keyof MediaFile, fileType: any) => {
    setImageIndex(idx);
    dispatch(settings.set({ getUploadFiles: 'open' }));
    setOpenFile({
      currentFileSrc: mediaFiles[idx][templateField],
      folder: folderName,
      type: fileType
    });
  };

  // ---------- Effects ----------
  useEffect(() => {
    if (allowMultipleMedia && lastChangedIndex !== null) {
      const updated = [...mediaFiles];
      const withNewRow = addNewMediaFilesRow(updated);
      setMediaFiles(withNewRow);
      setLastChangedIndex(null); // reset
    }
  }, [mediaFiles, allowMultipleMedia, lastChangedIndex]);

  useEffect(() => {
    setMediaFiles(addNewMediaFilesRow(mediaFiles));
  }, []);

  // ---------- Render ----------
  return (
    <>
      {getUploadFiles === 'open' && openFile && (
        <UploadFiles
          currentFilesSrc={openFile.currentFileSrc}
          folder={openFile.folder}
          handleClose={handleClose}
          handleFilesSubmit={handleSubmit}
          type={openFile.type}
          ownerId={ownerId}
        />
      )}

      {mediaTitle && <InputLabelContainer label={mediaTitle} />}

      <MediaInfoComponent
        changeImage={changeImage}
        deleteSelectedProduct={(file, idx) => handleDeleteMedia(idx)}
        fileTypes={libraryData?.result?.FileType}
        onFormItemChange={handleFormItemChange}
        mediaFiles={mediaFiles}
      />
    </>
  );
});
